import { devLog, alwaysLog } from '@/utils/logger';
import { driveClient } from '@/services/drive/DriveClient';
import { writeBlob, readAsBlob, removeIfExists, cleanExpiredFiles, removeAllFiles } from '@/utils/opfsIO';
import { CacheConfig } from '@/config/CacheConfig';
import { blobUrlCache } from '@/services/BlobUrlCache';

export interface MediaFileInfo {
  id: string; // 统一使用 driveId 作为主键
  name: string;
  mimeType: string;
  size: number;
  source: 'local' | 'drive';
  driveId?: string; // 如果是Drive文件，记录Drive ID
  r2Url?: string; // 大文件的 R2 公开地址；空则只走 Drive
  localPath?: string; // 如果是本地文件，记录路径
  blobUrl?: string; // 缓存的blob URL（会话级别，重启后失效）
  opfsPath?: string; // OPFS中的文件路径
  lastAccessed: number; // 最后访问时间
}

export interface CacheRecoveryStats {
  totalFiles: number;
  recoveredFromOPFS: number;
  recoveredFromDrive: number;
  failedRecovery: number;
  totalTime: number;
}

class MediaCacheService {
  private cache = new Map<string, MediaFileInfo>();
  private aliasMap = new Map<string, string>(); // 别名映射：旧格式 -> driveId
  private readonly MAX_CACHE_SIZE = 100; // 最大缓存文件数
  private readonly CACHE_EXPIRY = 24 * 60 * 60 * 1000; // 24小时过期
  
  // 缓存恢复相关属性
  private isRecovering = false;
  private recoveryStats: CacheRecoveryStats = {
    totalFiles: 0,
    recoveredFromOPFS: 0,
    recoveredFromDrive: 0,
    failedRecovery: 0,
    totalTime: 0
  };

  constructor() {
    this.loadFromStorage();
    this.migrateOldCacheFormat();
    // 启动时执行自查清理
    this.performStartupCleanup();
  }

  /**
   * 启动时执行自查清理
   * 1. 清理 media/ 中过期的缓存记录（基于 lastAccessed）
   * 2. 清理 media-decoded/ 中过期的文件（基于 lastModified）
   */
  private async performStartupCleanup(): Promise<void> {
    try {
      devLog.log('[MediaCacheService] 开始启动自查清理...');
      
      const ttlMs = (CacheConfig.opfsCache?.ttlDays || 7) * 24 * 60 * 60 * 1000;
      const now = Date.now();
      let cleanedCount = 0;

      // 1. 清理 media/ (基于缓存记录)
      const toRemove: string[] = [];
      for (const [id, fileInfo] of this.cache) {
        if (now - fileInfo.lastAccessed > ttlMs) {
          toRemove.push(id);
        }
      }

      const cleanupResults = await Promise.all(toRemove.map(async id => {
        const fileInfo = this.cache.get(id);
        if (!fileInfo) return 0;

        if (fileInfo.opfsPath) {
          try {
            await removeIfExists(fileInfo.opfsPath);
          } catch (e) {
            devLog.warn(`[MediaCacheService] 删除过期文件失败: ${fileInfo.opfsPath}`, e);
          }
        }
        if (fileInfo.blobUrl) {
          // 🎯 使用BlobUrlCache释放引用，而不是直接revoke
          // 假设 key 是 fileId-media（这是目前唯一的模式）
          const cacheKey = `${id}-media`;
          blobUrlCache.releaseKey(cacheKey);
          // 为了兼容旧逻辑或非cache管理的url，也可以尝试revoke，但主要靠 releaseKey
          // URL.revokeObjectURL(fileInfo.blobUrl); 
        }
        this.cache.delete(id);
        return 1;
      }));
      
      cleanedCount += cleanupResults.reduce((a, b) => a + b, 0);
      
      if (cleanedCount > 0) {
        this.saveToStorage();
        devLog.log(`[MediaCacheService] 清理了 ${cleanedCount} 个过期的 media/ 缓存项`);
      }

      // 2. 清理 media-decoded/ (基于文件修改时间)
      try {
        const decodedCleaned = await cleanExpiredFiles('media-decoded', ttlMs);
        if (decodedCleaned > 0) {
          devLog.log(`[MediaCacheService] 清理了 ${decodedCleaned} 个过期的 media-decoded/ 文件`);
        }
      } catch (e) {
        devLog.warn('[MediaCacheService] 清理 media-decoded/ 失败', e);
      }

      // 3. 🎯 新增：清理根目录下的残留文件 (如旧版本遗留的 .mp4 等)
      try {
        const rootCleaned = await cleanExpiredFiles('', ttlMs);
        if (rootCleaned > 0) {
          devLog.log(`[MediaCacheService] 清理了 ${rootCleaned} 个过期的根目录文件`);
        }
      } catch (e) {
        devLog.warn('[MediaCacheService] 清理根目录失败', e);
      }
      
      devLog.log('[MediaCacheService] 启动自查清理完成');
    } catch (error) {
      alwaysLog.error('[MediaCacheService] 启动自查清理失败:', error);
    }
  }

  /**
   * 添加文件到缓存
   */
  async addToCache(fileInfo: MediaFileInfo, blob?: Blob): Promise<void> {
    devLog.log(`[MediaCacheService] 添加文件到缓存: ${fileInfo.name}, ID: ${fileInfo.id}`);
    
    // 如果缓存已满，删除最旧的文件
    if (this.cache.size >= this.MAX_CACHE_SIZE) {
      await this.evictOldest();
    }

    // 如果提供了blob，直接保存到OPFS（使用Google Drive ID作为索引键）
    if (blob) {
      try {
        // 使用Google Drive ID作为OPFS路径，无需计算哈希值
        const opfsPath = `media/${fileInfo.id}`;
        await writeBlob(opfsPath, blob);
        const updatedFileInfo = {
          ...fileInfo,
          opfsPath
        };
        Object.assign(fileInfo, updatedFileInfo);
        devLog.log(`[MediaCacheService] 文件已保存到OPFS: ${opfsPath}, Drive ID: ${fileInfo.id}`);
      } catch (error) {
        devLog.warn(`[MediaCacheService] OPFS保存失败: ${error}`);
      }
    }

    this.cache.set(fileInfo.id, {
      ...fileInfo,
      lastAccessed: Date.now()
    });

    devLog.log(`[MediaCacheService] 缓存添加成功，当前缓存大小: ${this.cache.size}`);
    this.saveToStorage();
  }

  /**
   * 从缓存获取文件信息（支持别名查找）
   */
  getFromCache(fileId: string): MediaFileInfo | null {
    devLog.log(`[MediaCacheService] 查找缓存文件: ${fileId}`);
    devLog.log(`[MediaCacheService] 当前缓存大小: ${this.cache.size}`);
    
    // 首先尝试直接查找
    let fileInfo = this.cache.get(fileId);
    
    // 如果没找到，尝试通过别名查找
    if (!fileInfo) {
      const aliasedId = this.aliasMap.get(fileId);
      if (aliasedId) {
        devLog.log(`[MediaCacheService] 通过别名找到: ${fileId} -> ${aliasedId}`);
        fileInfo = this.cache.get(aliasedId);
      }
    }
    
    if (!fileInfo) {
      devLog.log(`[MediaCacheService] 缓存未找到: ${fileId}`);
      // 打印所有缓存键用于调试
      devLog.log(`[MediaCacheService] 所有缓存键:`, Array.from(this.cache.keys()));
      return null;
    }

    // 检查是否过期
    if (Date.now() - fileInfo.lastAccessed > this.CACHE_EXPIRY) {
      devLog.log(`[MediaCacheService] 缓存已过期: ${fileId}`);
      this.cache.delete(fileInfo.id);
      // 清理相关别名
      for (const [alias, id] of this.aliasMap.entries()) {
        if (id === fileInfo.id) {
          this.aliasMap.delete(alias);
        }
      }
      this.saveToStorage();
      return null;
    }

    devLog.log(`[MediaCacheService] 找到缓存文件: ${fileId}, 名称: ${fileInfo.name}`);
    
    // 更新访问时间
    fileInfo.lastAccessed = Date.now();
    this.cache.set(fileInfo.id, fileInfo);
    this.saveToStorage();

    return fileInfo;
  }

  /**
   * 添加别名映射（向后兼容）
   */
  addAlias(alias: string, driveId: string): void {
    this.aliasMap.set(alias, driveId);
    devLog.log(`[MediaCacheService] 添加别名映射: ${alias} -> ${driveId}`);
  }

  /**
   * 迁移旧格式的缓存项到新格式
   */
  private migrateOldCacheFormat(): void {
    const oldFormatEntries: Array<[string, MediaFileInfo]> = [];
    
    // 找出旧格式的缓存项
    for (const [id, fileInfo] of this.cache) {
      if (id.startsWith('drive-audio-') || id.startsWith('drive-video-') || id.startsWith('drive-srt-')) {
        oldFormatEntries.push([id, fileInfo]);
      }
    }
    
    if (oldFormatEntries.length > 0) {
      devLog.log(`[MediaCacheService] 发现 ${oldFormatEntries.length} 个旧格式缓存项，开始迁移...`);
      
      for (const [oldId, fileInfo] of oldFormatEntries) {
        // 提取 driveId
        const driveId = fileInfo.driveId;
        if (driveId) {
          // 创建新的缓存项，使用 driveId 作为主键
          const newFileInfo: MediaFileInfo = {
            ...fileInfo,
            id: driveId
          };
          
          // 删除旧项，添加新项
          this.cache.delete(oldId);
          this.cache.set(driveId, newFileInfo);
          
          // 添加别名映射
          this.aliasMap.set(oldId, driveId);
          
          devLog.log(`[MediaCacheService] 迁移: ${oldId} -> ${driveId}`);
        }
      }
      
      // 保存迁移后的数据
      this.saveToStorage();
      devLog.log(`[MediaCacheService] 迁移完成，共迁移 ${oldFormatEntries.length} 项`);
    }
  }

  /**
   * 检查blob URL是否仍然有效
   */
  async isBlobUrlValid(blobUrl: string): Promise<boolean> {
    return blobUrlCache.isBlobUrlValid(blobUrl);
  }

  /**
   * 从OPFS恢复文件
   */
  async restoreFromOPFS(fileInfo: MediaFileInfo): Promise<Blob | null> {
    if (!fileInfo.opfsPath) {
      return null;
    }

    try {
      devLog.log(`[MediaCacheService] 尝试从OPFS恢复文件: ${fileInfo.opfsPath}`);
      
      // 使用统一的OPFS IO工具读取文件
      // 避免重复创建 blob URL：如果已有且有效，直接返回该 blob
      const result = await readAsBlob(fileInfo.opfsPath, fileInfo.mimeType, !fileInfo.blobUrl);
      
      // 更新文件信息（仅在需要时创建新的 blob URL）
      if (!fileInfo.blobUrl) {
        fileInfo.blobUrl = result.blobUrl;
      }
      fileInfo.lastAccessed = Date.now();
      
      devLog.log(`[MediaCacheService] 从OPFS成功恢复文件: ${fileInfo.name}`);
      return result.blob;
    } catch (error) {
      alwaysLog.error(`[MediaCacheService] 从OPFS恢复文件失败: ${error}`);
      return null;
    }
  }

  /**
   * 从Drive重新获取文件
   */
  async fetchFromDrive(driveId: string, fileInfo: MediaFileInfo): Promise<Blob | null> {
    const maxRetries = 3;
    let lastError: Error | null = null;
    
    // eslint-disable-next-line no-await-in-loop
    for (let attempt = 1; attempt <= maxRetries; attempt++) {
      try {
        devLog.log(`[MediaCacheService] 第 ${attempt}/${maxRetries} 次尝试从Drive获取文件: ${fileInfo.name} (${driveId})`);
        
        // eslint-disable-next-line no-await-in-loop
        const blob = await driveClient.downloadFileBlob(driveId);
        
        if (!blob || blob.size === 0) {
          throw new Error('获取到的音频文件为空');
        }
        
        devLog.log(`[MediaCacheService] 成功从Drive获取文件: ${fileInfo.name}, 大小: ${blob.size} bytes`);
        
        // 🎯 统一由 BlobUrlCache 管理 objectURL，不再直接创建
        // 更新缓存并保存到OPFS（不包含 blobUrl，由 BlobUrlCache 统一管理）
        const updatedFileInfo = {
          ...fileInfo,
          lastAccessed: Date.now()
        };
        // eslint-disable-next-line no-await-in-loop
        await this.addToCache(updatedFileInfo, blob);

        return blob;
      } catch (error) {
        lastError = error instanceof Error ? error : new Error(String(error));
        devLog.warn(`[MediaCacheService] 第 ${attempt} 次尝试失败:`, lastError.message);
        
        if (attempt < maxRetries) {
          // 等待一段时间后重试，使用指数退避
          const delay = Math.min(1000 * 2 ** (attempt - 1), 5000);
          devLog.log(`[MediaCacheService] 等待 ${delay}ms 后重试...`);
          // eslint-disable-next-line no-await-in-loop
          await new Promise<void>(resolve => {
            setTimeout(() => resolve(), delay);
          });
        }
      }
    }
    
    alwaysLog.error(`[MediaCacheService] 连续 ${maxRetries} 次尝试失败，最终错误:`, lastError?.message);
    return null;
  }

  /**
   * 获取文件（优先从缓存，fallback到OPFS，最后到Drive）
   * 🎯 统一由 BlobUrlCache 管理 objectURL
   */
  async getFile(fileId: string): Promise<{ blob: Blob; blobUrl: string } | null> {
    const fileInfo = this.getFromCache(fileId);
    if (!fileInfo) {
      devLog.log(`[MediaCacheService] File not found in cache: ${fileId}`);
      return null;
    }

    // 1. 检查blob URL是否有效（会话级别）
    if (fileInfo.blobUrl) {
      const isValid = await this.isBlobUrlValid(fileInfo.blobUrl);
      if (isValid) {
        devLog.log(`[MediaCacheService] Using cached blob URL: ${fileInfo.name}`);
        // 获取blob内容
        const response = await fetch(fileInfo.blobUrl);
        const blob = await response.blob();
        return { blob, blobUrl: fileInfo.blobUrl };
      } else {
        devLog.log(`[MediaCacheService] Blob URL invalid, trying OPFS: ${fileInfo.name}`);
      }
    }

    // 2. blob URL无效，尝试从OPFS恢复
    if (fileInfo.opfsPath) {
      const blob = await this.restoreFromOPFS(fileInfo);
      if (blob) {
        // 🎯 统一通过 BlobUrlCache 获取 objectURL
        const cacheKey = `${fileId}-media`;
        const { url } = await blobUrlCache.acquire(cacheKey, async() => blob);
        
        // 更新缓存中的 blobUrl
        fileInfo.blobUrl = url;
        this.cache.set(fileId, fileInfo);
        this.saveToStorage();
        
        devLog.log(`[MediaCacheService] 从OPFS成功恢复文件: ${fileInfo.name}`);
        return { blob, blobUrl: url };
      }
    }

    // 3. OPFS恢复失败，根据文件来源决定是否尝试Drive回退
    if (fileInfo.source === 'drive' && fileInfo.driveId) {
      devLog.log(`[MediaCacheService] OPFS恢复失败，fetching from Drive: ${fileInfo.name}`);
      const blob = await this.fetchFromDrive(fileInfo.driveId, fileInfo);
      if (blob) {
        // 🎯 统一通过 BlobUrlCache 获取 objectURL
        const cacheKey = `${fileId}-media`;
        const { url } = await blobUrlCache.acquire(cacheKey, async() => blob);
        
        // 更新缓存中的 blobUrl
        fileInfo.blobUrl = url;
        this.cache.set(fileId, fileInfo);
        this.saveToStorage();
        
        return { blob, blobUrl: url };
      }
    } else if (fileInfo.source === 'local') {
      devLog.log(`[MediaCacheService] OPFS恢复失败，local file cannot recover: ${fileInfo.name}`);
      // 对于本地文件，清理无效的缓存项
      this.cache.delete(fileId);
      this.saveToStorage();
      return null;
    }

    alwaysLog.error(`[MediaCacheService] Failed to get file: ${fileId}`);
    return null;
  }

  /**
   * 删除最旧的文件
   */
  private async evictOldest(): Promise<void> {
    let oldestFile: MediaFileInfo | null = null;
    let oldestTime = Date.now();

    for (const [id, fileInfo] of this.cache) {
      if (fileInfo.lastAccessed < oldestTime) {
        oldestTime = fileInfo.lastAccessed;
        oldestFile = fileInfo;
      }
    }

    if (oldestFile) {
      // 释放blob URL
      if (oldestFile.blobUrl) {
        // 🎯 释放blob URL引用
        blobUrlCache.releaseKey(`${oldestFile.id}-media`);
      }
      
      // 删除OPFS文件
      if (oldestFile.opfsPath) {
        try {
          await removeIfExists(oldestFile.opfsPath);
          devLog.log(`[MediaCacheService] 删除OPFS文件: ${oldestFile.opfsPath}`);
        } catch (error) {
          devLog.warn(`[MediaCacheService] 删除OPFS文件失败: ${error}`);
        }
      }
      
      this.cache.delete(oldestFile.id);
    }
  }

  /**
   * 保存到本地存储
   */
  private saveToStorage(): void {
    try {
      const cacheData = Array.from(this.cache.entries());
      const aliasData = Array.from(this.aliasMap.entries());
      const storageData = {
        cache: cacheData,
        aliases: aliasData
      };
      localStorage.setItem('mediaCache', JSON.stringify(storageData));
    } catch (error) {
      alwaysLog.error('[MediaCacheService] Failed to save to storage:', error);
    }
  }

  /**
   * 从本地存储加载
   */
  private loadFromStorage(): void {
    try {
      const cacheData = localStorage.getItem('mediaCache');
      if (cacheData) {
        const data = JSON.parse(cacheData);
        
        // 兼容旧格式（只有 cache 数组）
        if (Array.isArray(data)) {
          this.cache = new Map(data);
        } else {
          // 新格式（包含 cache 和 aliases）
          this.cache = new Map(data.cache || []);
          this.aliasMap = new Map(data.aliases || []);
        }
        
        // 清理过期的文件
        const now = Date.now();
        for (const [id, fileInfo] of this.cache) {
          if (now - fileInfo.lastAccessed > this.CACHE_EXPIRY) {
            this.cache.delete(id);
          }
        }
        
        // 清理过期的别名
        for (const [alias, driveId] of this.aliasMap) {
          if (!this.cache.has(driveId)) {
            this.aliasMap.delete(alias);
          }
        }
      }
    } catch (error) {
      alwaysLog.error('[MediaCacheService] Failed to load from storage:', error);
    }
  }

  /**
   * 删除单个文件的缓存
   */
  async removeFromCache(fileId: string): Promise<void> {
    const fileInfo = this.cache.get(fileId);
    if (!fileInfo) return;

    devLog.log(`[MediaCacheService] 删除缓存文件: ${fileId}`);
    
    // 释放 blob URL
    if (fileInfo.blobUrl) {
      blobUrlCache.releaseKey(`${fileInfo.id}-media`);
    }

    // 删除 OPFS 文件
    if (fileInfo.opfsPath) {
      try {
        await removeIfExists(fileInfo.opfsPath);
      } catch (e) {
        devLog.warn(`[MediaCacheService] 删除OPFS文件失败: ${fileInfo.opfsPath}`, e);
      }
    }

    // 从内存中移除
    this.cache.delete(fileId);

    // 清理相关的别名
    for (const [alias, mappedId] of this.aliasMap.entries()) {
      if (mappedId === fileId) {
        this.aliasMap.delete(alias);
      }
    }

    this.saveToStorage();
  }

  /**
   * 清理缓存
   */
  async clearCache(): Promise<void> {
    devLog.log('[MediaCacheService] 开始彻底清理缓存 (内存 + OPFS)...');
    
    // 1. 清理 media/ 缓存
    const clearPromises = Array.from(this.cache.values()).map(async fileInfo => {
      // 释放blob URL
      if (fileInfo.blobUrl) {
        // 🎯 释放blob URL引用
        blobUrlCache.releaseKey(`${fileInfo.id}-media`);
      }
      
      // 删除 OPFS 文件
      if (fileInfo.opfsPath) {
        try {
          await removeIfExists(fileInfo.opfsPath);
        } catch (e) {
          devLog.warn(`[MediaCacheService] 删除文件失败: ${fileInfo.opfsPath}`, e);
        }
      }
    });

    await Promise.all(clearPromises);
    
    this.cache.clear();
    this.aliasMap.clear();
    localStorage.removeItem('mediaCache');

    // 2. 清理 media-decoded/ 目录
    try {
      const decodedCount = await removeAllFiles('media-decoded');
      devLog.log(`[MediaCacheService] 已清空 media-decoded/ 目录，共 ${decodedCount} 个文件`);
    } catch (e) {
      devLog.warn('[MediaCacheService] 清空 media-decoded/ 失败', e);
    }
    
    devLog.log('[MediaCacheService] 缓存清理完成');
  }

  /**
   * 获取缓存统计信息
   */
  getCacheStats(): { size: number; maxSize: number } {
    return {
      size: this.cache.size,
      maxSize: this.MAX_CACHE_SIZE
    };
  }

  /**
   * 获取所有缓存ID
   */
  getAllCacheIds(): string[] {
    return Array.from(this.cache.keys());
  }

  /**
   * 在页面加载时快速恢复缓存
   */
  async quickRecovery(): Promise<CacheRecoveryStats> {
    if (this.isRecovering) {
      devLog.log('[MediaCacheService] 恢复已在进行中，跳过');
      return this.recoveryStats;
    }

    this.isRecovering = true;
    const startTime = Date.now();
    
    try {
      devLog.log('[MediaCacheService] 开始快速缓存恢复...');
      
      // 获取所有缓存的文件信息
      const cacheStats = this.getCacheStats();
      this.recoveryStats.totalFiles = cacheStats.size;
      
      if (cacheStats.size === 0) {
        devLog.log('[MediaCacheService] 没有需要恢复的缓存文件');
        return this.recoveryStats;
      }

      // 并行恢复所有文件（限制并发数避免过载）
      const recoveryPromises: Promise<void>[] = [];
      const maxConcurrency = 3; // 限制并发数
      
      // 这里需要获取所有缓存的文件ID，但由于MediaCacheService没有提供获取所有文件的方法
      // 我们暂时跳过批量恢复，改为按需恢复
      
      devLog.log('[MediaCacheService] 快速恢复完成，将按需恢复文件');
    } catch (error) {
      alwaysLog.error('[MediaCacheService] 快速恢复失败:', error);
    } finally {
      this.isRecovering = false;
      this.recoveryStats.totalTime = Date.now() - startTime;
    }

    return this.recoveryStats;
  }

  /**
   * 按需恢复单个文件
   */
  async recoverFile(fileId: string): Promise<boolean> {
    try {
      devLog.log(`[MediaCacheService] 开始恢复文件: ${fileId}`);
      
      const fileInfo = this.getFromCache(fileId);
      if (!fileInfo) {
        devLog.log(`[MediaCacheService] 文件不在缓存中: ${fileId}`);
        return false;
      }

      // 尝试从OPFS恢复
      if (fileInfo.opfsPath) {
        const result = await this.getFile(fileId);
        if (result) {
          devLog.log(`[MediaCacheService] 成功从OPFS恢复文件: ${fileInfo.name}`);
          this.recoveryStats.recoveredFromOPFS++;
          return true;
        }
      }

      // 如果是Drive文件，尝试从Drive恢复
      if (fileInfo.source === 'drive' && fileInfo.driveId) {
        const result = await this.getFile(fileId);
        if (result) {
          devLog.log(`[MediaCacheService] 成功从Drive恢复文件: ${fileInfo.name}`);
          this.recoveryStats.recoveredFromDrive++;
          return true;
        }
      }

      devLog.log(`[MediaCacheService] 文件恢复失败: ${fileInfo.name}`);
      this.recoveryStats.failedRecovery++;
      return false;
    } catch (error) {
      alwaysLog.error(`[MediaCacheService] 恢复文件时出错: ${fileId}`, error);
      this.recoveryStats.failedRecovery++;
      return false;
    }
  }

  /**
   * 获取恢复统计信息
   */
  getRecoveryStats(): CacheRecoveryStats {
    return { ...this.recoveryStats };
  }

  /**
   * 重置恢复统计信息
   */
  resetRecoveryStats(): void {
    this.recoveryStats = {
      totalFiles: 0,
      recoveredFromOPFS: 0,
      recoveredFromDrive: 0,
      failedRecovery: 0,
      totalTime: 0
    };
  }

  /**
   * 检查是否正在恢复
   */
  isRecoveringInProgress(): boolean {
    return this.isRecovering;
  }
}

export const mediaCacheService = new MediaCacheService();
